<script setup lang="ts">
  import { computed, inject, onMounted, ref, type Ref } from 'vue'
  import { type ClientCase, type OnboardingPackageSessionDetail } from '@/types/generated'
  import { useTranslation } from '@/composables/useTranslation'
  import clientPortalItems from '@/components/clientPortal/clientPortalItemsV3.vue'

  const caseRecord = inject<Ref<ClientCase | null>>('caseRecord')
  const onboardingSession = inject<Ref<OnboardingPackageSessionDetail | null>>('onboardingSession')
  const { t } = useTranslation({
    en: {
      requiredActions: 'Required Actions',
      toProceedWithRequest:
        'to proceed with your request, please complete the following documents or questionnaires.'
    },
    de: {
      requiredActions: 'Handlung erforderlich',
      toProceedWithRequest:
        'um mit Ihrer Anfrage fortzufahren, füllen Sie bitte die folgenden Dokumente oder Fragebögen aus.'
    }
  })

  // Check if we're in an onboarding session context
  const isOnboardingSession = computed(() => !!onboardingSession?.value)

  const greeting = computed(() => {
    // For onboarding sessions, use the case from the session
    const caseData = isOnboardingSession.value ? onboardingSession?.value?.case : caseRecord?.value
    const caseWithClientNames = caseData as any
    if (caseWithClientNames?.client_first_name && caseWithClientNames?.client_last_name) {
      return `Hallo ${caseWithClientNames.client_first_name} ${caseWithClientNames.client_last_name},`
    } else if (caseWithClientNames?.client_first_name) {
      return `Hallo ${caseWithClientNames.client_first_name},`
    } else if (caseWithClientNames?.client_last_name) {
      return `Hallo ${caseWithClientNames.client_last_name},`
    }
    return 'Hallo,'
  })

  const allComplete = ref(true)
  const checkAllComplete = () => {
    // For onboarding sessions, check if there are any unstarted items (templates)
    if (isOnboardingSession.value && onboardingSession?.value) {
      const session = onboardingSession.value
      // If there are any unstarted questionnaires, forms, or pdf_form_templates, not complete
      if (
        session.questionnaires?.length > 0 ||
        session.forms?.length > 0 ||
        session.pdf_form_templates?.length > 0
      ) {
        allComplete.value = false
        return
      }
    }

    // For regular case records, check submissions
    if (caseRecord?.value?.questionnaire_submissions) {
      for (const submission of caseRecord.value.questionnaire_submissions) {
        if (submission.state !== 'CLOSED') {
          allComplete.value = false
          return
        }
      }
    }

    // Check all form requests if all submissions are complete
    const formRequests = isOnboardingSession.value
      ? onboardingSession?.value?.form_requests
      : caseRecord?.value?.form_requests
    if (allComplete.value && formRequests) {
      for (const request of formRequests) {
        if (!request.response_present) {
          allComplete.value = false
          return
        }
      }
    }

    // Check all PDF form submissions if all form requests are complete
    const pdfFormSubmissions = isOnboardingSession.value
      ? onboardingSession?.value?.pdf_form_submissions
      : caseRecord?.value?.pdf_form_submissions
    if (allComplete.value && pdfFormSubmissions) {
      for (const submission of pdfFormSubmissions) {
        if (submission.submission_status !== 'completed') {
          allComplete.value = false
          return
        }
      }
    }
  }

  onMounted(() => {
    checkAllComplete()
  })
</script>

<template>
  <div
    v-if="caseRecord || onboardingSession"
    data-cy="client-portal-home-view"
    class="flex min-h-full flex-col bg-gray-50"
  >
    <header
      data-cy="client-portal-header"
      class="px-6 py-6"
    >
      <div class="space-y-2">
        <h1
          data-cy="client-portal-title"
          tabindex="-1"
          class="text-xl font-semibold text-black"
        >
          {{ t('requiredActions') }}
        </h1>
        <p
          data-cy="client-portal-greeting"
          class="text-base leading-relaxed text-gray-700"
        >
          {{ greeting }} {{ t('toProceedWithRequest') }}
        </p>
      </div>
    </header>
    <section
      class="flex-1 p-6"
      :aria-label="t('requiredActions')"
    >
      <clientPortalItems />
    </section>
  </div>
</template>
